import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import ModalHeader from "@/components/ui/ModalHeader";
import SelectBox from "@/components/ui/SelectBox";
import { useCodeInsert, useCodeList } from "@/hooks";
import { useState, useEffect } from "react";
import { toast } from "sonner";

interface AddModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AddModal({ isOpen, onClose }: AddModalProps) {
  const [category, setCategory] = useState("1"); // "1": 1depth, "2": 2depth, "3": 3depth, "4": 4depth
  const [parent1, setParent1] = useState(""); // 2depth일 때 선택할 1depth 부모
  const [parent2, setParent2] = useState(""); // 3depth일 때 선택할 2depth 부모
  const [parent3, setParent3] = useState(""); // 4depth일 때 선택할 3depth 부모
  const [code, setCode] = useState("");
  const [codeName, setCodeName] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [useYn, setUseYn] = useState<"Y" | "N">("Y");
  const [remark, setRemark] = useState("");
  const [parent1Options, setParent1Options] = useState<Array<{ label: string; value: string }>>([]);
  const [parent2Options, setParent2Options] = useState<Array<{ label: string; value: string }>>([]);
  const [parent3Options, setParent3Options] = useState<Array<{ label: string; value: string }>>([]);
  const [errors, setErrors] = useState({
    category: false,
    code: false,
    codeName: false,
    parent1: false,
    parent2: false,
    parent3: false,
    sortOrder: false,
  });
  const [touched, setTouched] = useState({
    category: false,
    parent1: false,
    parent2: false,
    parent3: false,
    code: false,
    codeName: false,
    sortOrder: false,
  });

  const { mutate: insertCode } = useCodeInsert();

  // 1depth 목록 (부모 없음)
  const { data: data1 } = useCodeList({ parentId: undefined });

  // 2depth 목록 (parent1 선택 시)
  const parentId1 = parent1 ? parseInt(parent1, 10) : undefined;
  const { data: data2 } = useCodeList({ parentId: parentId1 }, { enabled: !!parentId1 });

  // 3depth 목록 (parent2 선택 시)
  const parentId2 = parent2 ? parseInt(parent2, 10) : undefined;
  const { data: data3 } = useCodeList({ parentId: parentId2 }, { enabled: !!parentId2 });

  // 4depth 목록 (parent3 선택 시)
  const parentId3 = parent3 ? parseInt(parent3, 10) : undefined;
  const { data: data4 } = useCodeList({ parentId: parentId3 }, { enabled: !!parentId3 });

  const useYnOptions = [
    { label: "사용", value: "Y" },
    { label: "미사용", value: "N" },
  ];

  const categoryOptions = [
    { label: "1depth", value: "1" },
    { label: "2depth", value: "2" },
    { label: "3depth", value: "3" },
    { label: "4depth", value: "4" },
  ];

  // 모달 open 시 초기화 + 1depth options 구성
  useEffect(() => {
    if (!isOpen) return;
    resetForm();
    if (Array.isArray(data1)) {
      const options = data1
        .slice()
        .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((item: any) => ({
          label: item.name,
          value: String(item.codeId),
        }));
      setParent1Options(options);
    } else {
      setParent1Options([]);
    }
  }, [isOpen, data1]);

  // parent1 변경 시 2depth options 구성
  useEffect(() => {
    if (Array.isArray(data2)) {
      const options = data2
        .slice()
        .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((item: any) => ({
          label: item.name,
          value: String(item.codeId),
        }));
      setParent2Options(options);
    } else {
      setParent2Options([]);
    }
  }, [data2]);

  // parent2 변경 시 3depth options 구성
  useEffect(() => {
    if (Array.isArray(data3)) {
      const options = data3
        .slice()
        .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((item: any) => ({
          label: item.name,
          value: String(item.codeId),
        }));
      setParent3Options(options);
    } else {
      setParent3Options([]);
    }
  }, [data3]);

  // 카테고리 전환 시 초기화/정렬순서 처리
  useEffect(() => {
    if (category === "1") {
      // 1depth: 부모 없음
      setParent1("");
      setParent2("");
      setParent3("");
      setErrors(prev => ({ ...prev, parent1: false, parent2: false, parent3: false }));
    } else if (category === "2" || category === "3" || category === "4") {
      // 2,3,4depth: sortOrder 비우기 (부모 선택 후 자동 설정)
      setSortOrder("");
      setTouched(prev => ({ ...prev, sortOrder: false }));
      setErrors(prev => ({ ...prev, sortOrder: false }));
    } else {
      // 미선택 상태
      setParent1("");
      setParent2("");
      setParent3("");
      setSortOrder("");
      setErrors(prev => ({ ...prev, parent1: false, parent2: false, parent3: false, sortOrder: false }));
    }
  }, [category]);

  // 1depth 선택 시: sortOrder 자동 지정
  useEffect(() => {
    if (category !== "1" || !Array.isArray(data1)) return;
    const maxSort = Math.max(...data1.map((d: any) => d.sortOrder || 0), 0);
    const next = String(maxSort + 1);
    setSortOrder(next);
    setTouched(prev => ({ ...prev, sortOrder: true }));
    setErrors(prev => ({ ...prev, sortOrder: false }));
  }, [category, data1]);

  // 2depth + parent1 선택 후: sortOrder 자동 지정
  useEffect(() => {
    if (category !== "2" || !parentId1 || !Array.isArray(data2)) return;
    const maxSort = Math.max(...data2.map((d: any) => d.sortOrder || 0), 0);
    const next = String(maxSort + 1);
    setSortOrder(next);
    setTouched(prev => ({ ...prev, sortOrder: true }));
    setErrors(prev => ({ ...prev, sortOrder: false }));
  }, [category, parentId1, data2]);

  // 3depth + parent2 선택 후: sortOrder 자동 지정
  useEffect(() => {
    if (category !== "3" || !parentId2 || !Array.isArray(data3)) return;
    const maxSort = Math.max(...data3.map((d: any) => d.sortOrder || 0), 0);
    const next = String(maxSort + 1);
    setSortOrder(next);
    setTouched(prev => ({ ...prev, sortOrder: true }));
    setErrors(prev => ({ ...prev, sortOrder: false }));
  }, [category, parentId2, data3]);

  // 4depth + parent3 선택 후: sortOrder 자동 지정
  useEffect(() => {
    if (category !== "4" || !parentId3 || !Array.isArray(data4)) return;
    const maxSort = Math.max(...data4.map((d: any) => d.sortOrder || 0), 0);
    const next = String(maxSort + 1);
    setSortOrder(next);
    setTouched(prev => ({ ...prev, sortOrder: true }));
    setErrors(prev => ({ ...prev, sortOrder: false }));
  }, [category, parentId3, data4]);

  // parent 변경 시 sortOrder 초기화
  useEffect(() => {
    if (category === "2" || category === "3" || category === "4") {
      setSortOrder("");
      setTouched(prev => ({ ...prev, sortOrder: false }));
      setErrors(prev => ({ ...prev, sortOrder: false }));
    }
  }, [parent1, parent2, parent3, category]);

  // 입력 reset
  const resetForm = () => {
    setCategory("1"); // 기본값을 1depth로 설정
    setParent1("");
    setParent2("");
    setParent3("");
    setCode("");
    setCodeName("");
    setSortOrder("");
    setUseYn("Y");
    setRemark("");
    setErrors({
      category: false,
      code: false,
      codeName: false,
      parent1: false,
      parent2: false,
      parent3: false,
      sortOrder: false,
    });
    setTouched({
      category: false,
      parent1: false,
      parent2: false,
      parent3: false,
      code: false,
      codeName: false,
      sortOrder: false,
    });
  };

  // 저장
  const handleSave = async () => {
    setTouched({
      category: true,
      parent1: true,
      parent2: true,
      parent3: true,
      code: true,
      codeName: true,
      sortOrder: true,
    });

    const so = Number(sortOrder);
    const nextErrors = {
      category: !(category === "1" || category === "2" || category === "3" || category === "4"),
      code: !code.trim(),
      codeName: !codeName.trim(),
      parent1: (category === "2" || category === "3" || category === "4") && !parent1,
      parent2: (category === "3" || category === "4") && !parent2,
      parent3: category === "4" && !parent3,
      sortOrder: !sortOrder.trim() || !Number.isInteger(so) || so <= 0,
    };

    setErrors(nextErrors);

    // 정렬순서가 유효해야만 진행
    if (nextErrors.sortOrder) return;

    // 다른 필드 에러가 있으면 중단
    const { sortOrder: _omit, ...others } = nextErrors;
    if (Object.values(others).some(Boolean)) return;

    // 중복 순서 체크
    let currentList: any[] = [];
    if (category === "1") {
      currentList = Array.isArray(data1) ? data1 : [];
    } else if (category === "2") {
      currentList = Array.isArray(data2) ? data2 : [];
    } else if (category === "3") {
      currentList = Array.isArray(data3) ? data3 : [];
    } else if (category === "4") {
      currentList = Array.isArray(data4) ? data4 : [];
    }

    const usedOrders = currentList.map((d: any) => Number(d.sortOrder) || 0);
    if (usedOrders.includes(so)) {
      toast.error(`정렬순서 ${so}는 이미 사용 중입니다. 다른 번호를 입력하세요.`);
      setErrors(prev => ({ ...prev, sortOrder: true }));
      return;
    }

    // parentId 결정
    let finalParentId: number | undefined;
    if (category === "1") {
      finalParentId = undefined;
    } else if (category === "2") {
      finalParentId = parentId1;
    } else if (category === "3") {
      finalParentId = parentId2;
    } else if (category === "4") {
      finalParentId = parentId3;
    }

    insertCode(
      {
        parentId: finalParentId,
        code: code.trim(),
        name: codeName.trim(),
        sortOrder: so,
        useYn,
        remark: remark.trim(),
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
      }
    );
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title="공통코드 추가">
      <ModalHeader title="기본 정보" />

      {/* 분류 구분 */}
      <div className="grid grid-cols-1 gap-3 mb-4">
        <div className="space-y-4">
          <SelectBox
            required
            label="분류 구분"
            value={category}
            onChange={v => {
              setCategory(v);
              setParent1("");
              setParent2("");
              setParent3("");
              setTouched(prev => ({ ...prev, category: true }));
              setErrors(prev => ({ ...prev, category: !(v === "1" || v === "2" || v === "3" || v === "4") }));
            }}
            options={categoryOptions}
            invalid={touched.category && errors.category}
          />
        </div>
      </div>

      {/* 2depth: 1depth 부모만 */}
      {category === "2" && (
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div className="space-y-4">
            <SelectBox
              required
              label="1depth 부모"
              value={parent1}
              onChange={v => {
                setParent1(v);
                setTouched(prev => ({ ...prev, parent1: true }));
                setErrors(prev => ({ ...prev, parent1: !v }));
              }}
              options={parent1Options}
              invalid={touched.parent1 && errors.parent1}
            />
          </div>
        </div>
      )}

      {/* 3depth: 1depth, 2depth 부모 */}
      {category === "3" && (
        <>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <SelectBox
                required
                label="1depth 부모"
                value={parent1}
                onChange={v => {
                  setParent1(v);
                  setParent2("");
                  setTouched(prev => ({ ...prev, parent1: true }));
                  setErrors(prev => ({ ...prev, parent1: !v }));
                }}
                options={parent1Options}
                invalid={touched.parent1 && errors.parent1}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <SelectBox
                required
                label="2depth 부모"
                value={parent2}
                disabled={!parent1}
                onChange={v => {
                  setParent2(v);
                  setTouched(prev => ({ ...prev, parent2: true }));
                  setErrors(prev => ({ ...prev, parent2: !v }));
                }}
                options={parent2Options}
                invalid={touched.parent2 && errors.parent2}
              />
            </div>
          </div>
        </>
      )}

      {/* 4depth: 1depth, 2depth, 3depth 부모 */}
      {category === "4" && (
        <>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <SelectBox
                required
                label="1depth 부모"
                value={parent1}
                onChange={v => {
                  setParent1(v);
                  setParent2("");
                  setParent3("");
                  setTouched(prev => ({ ...prev, parent1: true }));
                  setErrors(prev => ({ ...prev, parent1: !v }));
                }}
                options={parent1Options}
                invalid={touched.parent1 && errors.parent1}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <SelectBox
                required
                label="2depth 부모"
                value={parent2}
                disabled={!parent1}
                onChange={v => {
                  setParent2(v);
                  setParent3("");
                  setTouched(prev => ({ ...prev, parent2: true }));
                  setErrors(prev => ({ ...prev, parent2: !v }));
                }}
                options={parent2Options}
                invalid={touched.parent2 && errors.parent2}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 mb-4">
            <div className="space-y-4">
              <SelectBox
                required
                label="3depth 부모"
                value={parent3}
                disabled={!parent2}
                onChange={v => {
                  setParent3(v);
                  setTouched(prev => ({ ...prev, parent3: true }));
                  setErrors(prev => ({ ...prev, parent3: !v }));
                }}
                options={parent3Options}
                invalid={touched.parent3 && errors.parent3}
              />
            </div>
          </div>
        </>
      )}

      <div className="flex gap-3 mb-4">
        <div className="w-1/3">
          <InputBox
            required
            label="분류 코드"
            value={code}
            onChange={e => {
              const v = e.target.value;
              setCode(v);
              setTouched(prev => ({ ...prev, code: true }));
              if (v.trim()) setErrors(prev => ({ ...prev, code: false }));
              else setErrors(prev => ({ ...prev, code: true }));
            }}
            className={touched.code && errors.code ? "border-[#FF3232]" : ""}
          />
        </div>
        <div className="w-2/3">
          <InputBox
            required
            label="분류 명칭"
            value={codeName}
            onChange={e => {
              const v = e.target.value;
              setCodeName(v);
              setTouched(prev => ({ ...prev, codeName: true }));
              setErrors(prev => ({ ...prev, codeName: !v.trim() }));
            }}
            className={touched.codeName && errors.codeName ? "border-[#FF3232]" : ""}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-4">
        <div className="space-y-4">
          <InputBox label="비고" value={remark} onChange={e => setRemark(e.target.value)} />
        </div>
      </div>

      <ModalHeader title="추가 정보" />
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
        <div className="space-y-4">
          <InputBox
            required
            label="정렬순서 (1부터)"
            value={sortOrder}
            onChange={e => {
              const v = e.target.value;
              setSortOrder(v);
              setTouched(prev => ({ ...prev, sortOrder: true }));
              const n = Number(v);
              const invalid = !v.trim() || !Number.isInteger(n) || n <= 0;
              setErrors(prev => ({ ...prev, sortOrder: invalid }));
            }}
            className={touched.sortOrder && errors.sortOrder ? "border-[#FF3232]" : ""}
          />
        </div>
        <div className="space-y-4">
          <SelectBox label="사용 여부" value={useYn} onChange={v => setUseYn(v as "Y" | "N")} options={useYnOptions} />
        </div>
      </div>

      {/* 모달 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className="py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleSave} className="py-[10px]">
            저장
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
